import { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  OUTCOME,
  initFailureState,
  applyOutcome,
  getFailureThresholds,
} from "./utils/verificationFailure";

export const AppContext = createContext();

export const useAppContext = () => useContext(AppContext);

export const AppProvider = ({
  children,
  referenceNo,
  timer,
  allDetails,
  agentCode,
  currentPage,
  encryptedText,
  HeaderSectionHeight,
  onQnaFromJourney,
}) => {
  const [journeyData, setJourneyData] = useState(null);
  const [journeyConfig, setJourneyConfig] = useState(null);
  const [selectedLanguageCode, setSelectedLanguageCode] = useState("");
  const [isStartingJourneyClick, setIsStartingJourneyClick] = useState(false)
  const [isCameraPermissionAllowed, setIsCameraPermissionAllowed] =
    useState(null);
  const [mediaError, setMediaError] = useState(null);
  const [isMicrophonePermissionAllowed, setIsMicrophonePermissionAllowed] =
    useState(null);
  const [isLocationPermissionAllowed, setIsLocationPermissionAllowed] =
    useState(null);
  const [formattedTime, setFormattedTime] = useState("10:00");
  const [isMeetingEnded, setIsMeetingEnded] = useState(false);
  const [screenshots, setScreenshots] = useState([]);
  const [qna, setQna] = useState(null)
  const applicationNo = referenceNo;
  const currentPageRef = useRef()

  // PUBSUB STATES
  const [allPubsubData, setAllPubsubData] = useState(null);
  const [journeyConfigData, setJourneyConfigData] = useState(null);
  const [journeyPubsubData, setJourneyPubsubData] = useState(null);
  const [faceVerified, setFaceVerified] = useState(false);
  const [agreedData, setAgreedData] = useState(false);
  const [faceDetectError, setFaceDetectError] = useState(null);
  const [currentPageData, setCurrentPageData] = useState(null);
  const [mediaPermissionEror, setMediaPermissionError] = useState(null);
  const [isAgreeButtonClick, setIsAgreeButtonClick] = useState(null);
  const [isDisagreeButtonClick, setIsDisagreeButtonClick] = useState(null);
  const [isTimerRunningData, setIsTimerRunningData] = useState(false);
  const [timerData, setTimerData] = useState(0)
  const [runningTimeData, setRunningTimeData] = useState("10:00")
  const [selectedLang, setSelectedLang] = useState("")
  const [isVideoConsentStart, setIsVideoConsentStart] = useState(false)
  const [openVideoErr, setOpenVideoErr] = useState(false)
  const [openDeviceChange, setOpenDeviceChange] = useState(false)
  const [openVideoFreeze, setOpenVideoFreeze] = useState(false)
  const [openVideoFailErr, setOpenVideoFailErr] = useState(false);
  const [openTextMatchErr, setOpenTextMatchErr] = useState(false);
  const [openErrorBox, setOpenErrorBox] = useState(false)
  const [openJourneyLimitBox, setOpenJourneyLimitBox] = useState(false)
  const [isAbortSignal, setIsAbortSignal] = useState(false)
  const [isJourneyEnded, setIsJourneyEnded] = useState(false)
  const endApiCalledRef = useRef()
  const [deviceData, setDeviceData] = useState({
    latitude: null,
    longitude: null,
    browserName: null,
    deviceName: null,
    network: null,
  });
  const [accuracy, setAccuracy] = useState(null)
  const [qnaPubSubData, setQnaPubSubData] = useState({});
  const [terminationFlag, setTerminationFlag] = useState(false);
  // Recorder<->client API delegation (bandwidth-saving fallback)
  const [delegatedApi, setDelegatedApi] = useState(null);
  const [delegatedApiSuccess, setDelegatedApiSuccess] = useState(null);
  // Recorder's own facematch reference frame; persists across per-page remounts.
  const recorderRefImageRef = useRef(null);
  // True when the customer's outgoing video bitrate has been 0 for 5s on the
  // VideoConsent step — used to disable the Start button until it recovers.
  const [isNetworkUnstable, setIsNetworkUnstable] = useState(false);
  // True when the RECORDER observes the customer's INCOMING video bitrate at ~0
  // for 5s on the VideoConsent step. On the recorder this is set directly; on
  // the client it is mirrored from the recorder via the NETWORK_UNSTABLE pubsub
  // channel. Drives the blocking "network unstable" dialog on both screens and
  // (combined with isNetworkUnstable) the consent stop/disable behaviour.
  const [isRecorderNetworkUnstable, setIsRecorderNetworkUnstable] =
    useState(false);

  // ---- recorder participant stats panel inputs (all describe the customer) ----
  const [deviceInfo, setDeviceInfo] = useState({
    microphone: null,
    speaker: null,
    camera: null,
  });
  // The mic the customer picked in the device picker. The mic is only turned on
  // at the VideoConsent screen, so the picker does NOT call VideoSDK's
  // changeMic() (there is no live mic track to switch on the earlier screens).
  // Instead it stashes the chosen deviceId here, and VideoConsent passes it to
  // createMicrophoneAudioTrack() when it enables the mic. "" => SDK default.
  const [selectedMicId, setSelectedMicId] = useState("");
  // The webcam the customer picked in the device picker. Unlike the mic, the
  // camera IS live on every screen, so switching applies immediately via
  // VideoSDK. But the actual track lives at the meeting level while the picker
  // (DeviceControls) is remounted per page — so the chosen id is stashed here to
  // survive navigation, otherwise the dropdown snaps back to the first camera on
  // each page even though the video still shows the picked one. "" => first cam.
  const [selectedWebcamId, setSelectedWebcamId] = useState("");
  // The speaker the customer picked in the device picker. VideoSDK has no
  // changeSpeaker, so routing is done by calling setSinkId on the page's media
  // elements — and those elements are recreated on every journey page. Like the
  // camera id above, this has to outlive the remounted picker: kept locally it
  // reset to "" on each navigation, the picker re-defaulted to speaker [0], and
  // the sink was genuinely re-routed to the default device. "" => first speaker.
  const [selectedSpeakerId, setSelectedSpeakerId] = useState("");
  // onQualityLimitation only fires on a problem (state:"detected") and again when
  // that problem clears (state:"resolved") — silence means healthy. So these
  // start at the healthy value rather than null: no event ever received reads as
  // Normal / Stable, a "detected" flips the row for that type, and the matching
  // "resolved" brings it back.
  const [cpuStatus, setCpuStatus] = useState("Normal"); // "Normal" | "High" — from onQualityLimitation type:"cpu"
  const [networkStatus, setNetworkStatus] = useState("Stable"); // "Stable" | "Unstable" — from onQualityLimitation bandwidth/congestion
  // /detect-spoof. `score` is its `accuracy` field — a liveness confidence, high
  // means real — paired with the verdict, so the panel badges off the API rather
  // than a threshold on the number.
  const [livenessResult, setLivenessResult] = useState(null); // {score: 0..100|null, isLive} | null
  // /verify. NOT to be confused with `accuracy` above (line 68), which is the
  // consent-video match accuracy off the VIDEO_RECORDING_ATTEMPT_ERR channel.
  const [faceMatchScore, setFaceMatchScore] = useState(null); // 0..100 | null
  // null until the /verify (face match) API has actually answered. Distinct from
  // agreedData/faceVerified, which the pipeline also sets on PersonalDetails
  // (where no match is possible) and clears on unrelated failures.
  const [faceMatchVerdict, setFaceMatchVerdict] = useState(null); // {verified, timestamp} | null

  // ---- Action-on-Failure: journey-wide verification-failure counters -------
  // One global cumulative counter + per-API consecutive counters (see
  // verificationFailure.js). api_error is never counted. Kept in refs so they
  // survive the per-page remounts that reset the recorder's delegation counters;
  // `suspectedFraud` is the state MeetingContainer watches to run the teardown.
  const failureThresholdsRef = useRef(getFailureThresholds());
  const journeyFailureStateRef = useRef(initFailureState());
  const seenOutcomeIdsRef = useRef(new Set());
  const suspectedFraudRef = useRef(false);
  const [suspectedFraud, setSuspectedFraud] = useState(null);

  // Fold one classified verdict into the counters. Called by ClientPubSubHandler
  // (recorder-published outcomes, deduped by message id) and by ParticipantView
  // (its own delegated-run outcomes). Trips `suspectedFraud` once, on breach.
  const recordVerificationOutcome = (apiName, outcome, dedupeId) => {
    if (suspectedFraudRef.current) return;
    if (dedupeId != null) {
      if (seenOutcomeIdsRef.current.has(dedupeId)) return;
      seenOutcomeIdsRef.current.add(dedupeId);
    }
    const { state, breached, reason } = applyOutcome(
      journeyFailureStateRef.current,
      apiName,
      outcome,
      failureThresholdsRef.current,
    );
    journeyFailureStateRef.current = state;

    // --- debug visibility: colour-coded per outcome so failures stand out ---
    const th = failureThresholdsRef.current;
    const cc = state.consecutive;
    const colour =
      outcome === OUTCOME.REAL_FAIL
        ? "#e11d48" // red  — a counted failure
        : outcome === OUTCOME.PASS
          ? "#16a34a" // green — success, resets that streak
          : "#9ca3af"; // grey — api_error, never counted
    console.log(
      `%c FRAUD %c ${apiName} → ${outcome} %c cumulative ${state.cumulative}/${th.cumulative} · faces ${cc.faces}/${th.faces} · spoof ${cc.spoof}/${th.spoof} · match ${cc.match}/${th.match} `,
      "color:#fff;background:#1f2937;padding:2px 5px;border-radius:3px 0 0 3px;font-weight:bold",
      `color:#fff;background:${colour};padding:2px 6px;font-weight:bold`,
      "color:#374151;background:#f3f4f6;padding:2px 6px;border-radius:0 3px 3px 0",
    );

    if (breached) {
      console.log(
        `%c 🚨 SUSPECTED_FRAUD — ${reason} threshold hit — terminating journey `,
        "color:#fff;background:#b91c1c;padding:3px 9px;border-radius:3px;font-weight:bold;font-size:13px",
      );
      suspectedFraudRef.current = true;
      setSuspectedFraud({ reason });
    }
  };

  useEffect(() => {
    currentPageRef.current = currentPage
  }, [currentPage])

  return (
    <AppContext.Provider
      value={{
        referenceNo,
        journeyConfig,
        applicationNo,
        allDetails,
        journeyData,
        timer,
        agentCode,
        currentPage,
        selectedLanguageCode,
        isCameraPermissionAllowed,
        isMicrophonePermissionAllowed,
        isLocationPermissionAllowed,
        formattedTime,
        encryptedText,
        HeaderSectionHeight,
        isMeetingEnded,
        allPubsubData,
        faceVerified,
        agreedData,
        faceDetectError,
        currentPageData,
        mediaPermissionEror,
        isAgreeButtonClick,
        isDisagreeButtonClick,
        isTimerRunningData,
        journeyConfigData,
        journeyPubsubData,
        timerData,
        runningTimeData,
        selectedLang,
        isVideoConsentStart,
        openVideoErr,
        openVideoFailErr,
        isAbortSignal,
        deviceData,
        isStartingJourneyClick,
        isJourneyEnded,
        mediaError,
        openDeviceChange,
        openVideoFreeze,
        openErrorBox,
        openJourneyLimitBox,
        screenshots,
        openTextMatchErr,
        currentPageRef,
        accuracy,
        qna,
        terminationFlag,
        delegatedApi,
        delegatedApiSuccess,
        recorderRefImageRef,
        isNetworkUnstable,
        isRecorderNetworkUnstable,
        deviceInfo,
        selectedMicId,
        selectedWebcamId,
        selectedSpeakerId,
        cpuStatus,
        networkStatus,
        livenessResult,
        faceMatchScore,
        faceMatchVerdict,

        setFaceMatchVerdict,
        setIsRecorderNetworkUnstable,
        setDeviceInfo,
        setSelectedMicId,
        setSelectedWebcamId,
        setSelectedSpeakerId,
        setCpuStatus,
        setNetworkStatus,
        setLivenessResult,
        setFaceMatchScore,
        setIsNetworkUnstable,
        setQna,
        setDelegatedApi,
        setDelegatedApiSuccess,
        setJourneyData,
        setJourneyConfig,
        setSelectedLanguageCode,
        setIsCameraPermissionAllowed,
        setIsMicrophonePermissionAllowed,
        setIsLocationPermissionAllowed,
        setFormattedTime,
        setIsMeetingEnded,
        setAllPubsubData,
        setFaceVerified,
        setAgreedData,
        setFaceDetectError,
        setCurrentPageData,
        setMediaPermissionError,
        setIsAgreeButtonClick,
        setIsDisagreeButtonClick,
        setIsTimerRunningData,
        setJourneyConfigData,
        setJourneyPubsubData,
        setTimerData,
        setRunningTimeData,
        setSelectedLang,
        setIsVideoConsentStart,
        setOpenVideoErr,
        setOpenVideoFailErr,
        setIsAbortSignal,
        setDeviceData,
        setIsStartingJourneyClick,
        setIsJourneyEnded,
        setMediaError,
        setOpenDeviceChange,
        setOpenVideoFreeze,
        setOpenErrorBox,
        setScreenshots,
        setOpenTextMatchErr,
        endApiCalledRef,
        setAccuracy,
        setOpenJourneyLimitBox,
        qnaPubSubData,
        setQnaPubSubData,
        onQnaFromJourney,
        setTerminationFlag,
        suspectedFraud,
        setSuspectedFraud,
        recordVerificationOutcome,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
